import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Button,
} from '@mui/material';
import {
  Dashboard,
  Analytics,
  Article,
  AttachMoney,
  Person,
  ChevronLeft,
  ChevronRight,
  DarkMode,
  LightMode,
  Storefront,
  Favorite,
  RequestPage,
  Handshake,
  Campaign,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageSwitcher from '../LanguageSwitcher';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const logo = require('../logo.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const logoBlack = require('../logo_black.png');

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const menuItemDefs = [
  { key: 'dashboard', icon: <Dashboard />, path: '/creator' },
  { key: 'analytics', icon: <Analytics />, path: '/creator/analytics' },
  { key: 'contents', icon: <Article />, path: '/creator/contents' },
  { key: 'profile', icon: <Person />, path: '/creator/profile' },
  { key: 'share2dm', icon: <Campaign />, path: '/creator/share2dm' },
  { key: 'divider', icon: null, path: '' },
  { key: 'marketplace', icon: <Storefront />, path: '/creator/marketplace' },
  { key: 'myWishlist', icon: <Favorite />, path: '/creator/wishlist' },
  { key: 'myRequests', icon: <RequestPage />, path: '/creator/requests' },
  { key: 'brandPartners', icon: <Handshake />, path: '/creator/brand-partners' },
  { key: 'myEarnings', icon: <AttachMoney />, path: '/creator/earnings' },
];

function Header({ onLogout, isDarkMode }: { onLogout: () => void; isDarkMode: boolean }) {
  const { t } = useTranslation();
  const [nickname, setNickname] = useState<string>('Creator');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let fetchDone = false;

    const fetchUserProfile = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          const { data: sellerProfile } = await supabase
            .from('seller_profile')
            .select('store_name')
            .eq('user_id', session.user.id)
            .single();

          setNickname(sellerProfile?.store_name || 'Creator');
        }
      } catch {
        setNickname('Creator');
      } finally {
        fetchDone = true;
        setLoading(false);
      }
    };

    fetchUserProfile();

    const fallbackTimer = setTimeout(() => {
      if (fetchDone) return;
      setNickname('Creator');
      setLoading(false);
    }, 3000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
        borderRadius: 3,
        px: 3,
        py: 1.2,
        minHeight: 68,
        boxSizing: 'border-box',
        boxShadow: isDarkMode ? '0px 2px 4px rgba(0,0,0,0.3)' : '0px 2px 4px rgba(0,0,0,0.05)',
        border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        mb: 2,
      }}
    >
      <Box>
        <img
          src={isDarkMode ? logoBlack : logo}
          alt="Clozet Logo"
          style={{ height: 48, width: 'auto', objectFit: 'contain', marginLeft: 2 }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ textAlign: 'right', mr: 1.2 }}>
          <Typography fontWeight={600} fontSize={15} color={isDarkMode ? '#fff' : '#000'}>
            {loading ? '...' : nickname}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
            <LanguageSwitcher isDarkMode={isDarkMode} />
            <Button
              variant="text"
              size="small"
              sx={{ color: isDarkMode ? '#ccc' : '#666', fontWeight: 500, textTransform: 'none', fontSize: 13, minWidth: 0, p: 0 }}
              onClick={onLogout}
            >
              {t('common.logout')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function CreatorLayout() {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) setIsDarkMode(savedDarkMode === 'true');
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [isDarkMode]);

  const handleDarkModeToggle = () => {
    setIsDarkMode(prev => {
      localStorage.setItem('darkMode', String(!prev));
      return !prev;
    });
  };

  const handleLogout = () => {
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth') || key.includes('session')) {
        localStorage.removeItem(key);
      }
    });
    window.location.replace('/login');
    supabase.auth.signOut().catch(() => {});
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ minHeight: 20, p: 0, mb: 0 }} />
      <Box sx={{ px: 2, py: 0.3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {!sidebarCollapsed && (
          <Typography variant="subtitle1" fontWeight={700} color={isDarkMode ? '#fff' : '#000'}>
            {t('common.menu')}
          </Typography>
        )}
        <ListItemButton
          onClick={() => setSidebarCollapsed(p => !p)}
          sx={{ color: isDarkMode ? '#ccc' : '#666', minWidth: 0, p: 0.5, borderRadius: 1 }}
        >
          {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </ListItemButton>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <List sx={{ flex: 1 }}>
        {menuItemDefs.map((item, index) => {
          if (item.key === 'divider') {
            return (
              <Box key={`divider-${index}`} sx={{ my: 1 }}>
                <Divider />
                {!sidebarCollapsed && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2, pt: 1.5, pb: 0.5, display: 'block',
                      color: isDarkMode ? '#9ca3af' : '#6b7280',
                      fontWeight: 600, fontSize: '0.7rem',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}
                  >
                    {t('nav.seller.affiliate')}
                  </Typography>
                )}
              </Box>
            );
          }

          return (
            <ListItem key={item.key} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2, mx: 1,
                  bgcolor: location.pathname === item.path ? (isDarkMode ? '#404040' : '#f5f5f5') : 'transparent',
                  '&.Mui-selected': {
                    bgcolor: isDarkMode ? '#404040' : '#f0f0f0',
                    color: isDarkMode ? '#fff' : '#000',
                    fontWeight: 700,
                  },
                  minHeight: 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon sx={{
                  color: location.pathname === item.path ? (isDarkMode ? '#fff' : '#000') : (isDarkMode ? '#ccc' : '#666'),
                  minWidth: sidebarCollapsed ? 24 : 36,
                }}>
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={t(`nav.seller.${item.key}`)}
                    sx={{
                      '& .MuiListItemText-primary': { color: isDarkMode ? '#fff' : '#000' },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ p: 2, mt: 'auto' }}>
        <ListItemButton
          onClick={handleDarkModeToggle}
          sx={{
            borderRadius: 2, bgcolor: 'transparent',
            '&:hover': { bgcolor: isDarkMode ? '#404040' : '#f5f5f5' },
            minHeight: 48,
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          }}
        >
          <ListItemIcon sx={{ color: isDarkMode ? '#ccc' : '#666', minWidth: sidebarCollapsed ? 24 : 36 }}>
            {isDarkMode ? <LightMode /> : <DarkMode />}
          </ListItemIcon>
          {!sidebarCollapsed && (
            <ListItemText
              primary={isDarkMode ? t('common.lightMode') : t('common.darkMode')}
              sx={{ '& .MuiListItemText-primary': { color: isDarkMode ? '#fff' : '#000' } }}
            />
          )}
        </ListItemButton>
        {sidebarCollapsed && (
          <Box sx={{ mt: 1 }}>
            <LanguageSwitcher isDarkMode={isDarkMode} collapsed />
          </Box>
        )}
        {!sidebarCollapsed && (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: isDarkMode ? '#555' : '#bbb', fontSize: '0.7rem' }}>
            v{process.env.REACT_APP_VERSION || require('../../../package.json').version}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{
      width: '100vw', minHeight: '100vh',
      bgcolor: isDarkMode ? '#1a1a1a' : '#f8f9fa',
      display: 'flex', flexDirection: 'row',
      transition: 'background-color 0.3s ease',
    }}>
      <CssBaseline />
      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', zIndex: 1201 }}>
        <Header onLogout={handleLogout} isDarkMode={isDarkMode} />
      </Box>
      <Box
        component="nav"
        sx={{
          width: { sm: sidebarCollapsed ? collapsedDrawerWidth + 16 : drawerWidth + 16 },
          flexShrink: { sm: 0 },
          p: 0,
          mt: '110px',
          transition: 'width 0.3s ease-in-out',
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              bgcolor: isDarkMode ? '#2d2d2d' : '#ffffff',
              border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0',
              borderRadius: 4,
              boxShadow: isDarkMode ? '0px 2px 4px rgba(0,0,0,0.3)' : '0px 2px 4px rgba(0,0,0,0.05)',
              top: '110px',
              height: 'calc(100vh - 110px)',
              m: 0,
              transition: 'width 0.3s ease-in-out, background-color 0.3s ease',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          width: { sm: `calc(100% - ${sidebarCollapsed ? collapsedDrawerWidth + 16 : drawerWidth + 16}px)` },
          mt: { xs: 16, sm: 16 },
          bgcolor: isDarkMode ? '#1a1a1a' : 'transparent',
          transition: 'width 0.3s ease-in-out, background-color 0.3s ease',
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
